import { min } from 'lib/array/arrayUtils'

const METASEARCH_PRICE_ACCURACY_CATEGORY = 'metasearch_price_accuracy'

type MetasearchPriceAccuracyPageType = 'spp' | 'legacy_bedbank'

interface SubjectParams {
  offerId: string
  rateType: string
  supplier?: string
  pageType: MetasearchPriceAccuracyPageType
  metasearchRoomRateId?: string
  partnerRoomRateId?: string
}

interface BedbankEventDataParams {
  offerId: string
  pageType: MetasearchPriceAccuracyPageType
  metasearchRoomRateId?: string
  rate: App.BedbankRate | undefined
  price?: number
  discountAmount?: number
}

interface PropertyEventDataParams {
  offerId: string
  pageType: MetasearchPriceAccuracyPageType
  metasearchRoomRateId?: string
  rate: App.EnrichedPropertyRate | undefined
  discountAmount?: number
}

export interface MetasearchPriceAccuracyEventData {
  price: number
  subject: string
}

export function getBedbankRatePublicTotal(rate: App.BedbankRate) {
  return rate.totals.inclusive + (rate.totals.propertyFees ?? 0)
}

function getDiscountedPrice(priceBeforeDiscount: number, discountAmount = 0) {
  return Math.max(priceBeforeDiscount - discountAmount, 0)
}

export function getCheapestBedbankRateForMetasearchPriceAccuracy(
  rates: Array<App.BedbankRate>,
): App.BedbankRate | undefined {
  return min(rates, getBedbankRatePublicTotal)
}

function encodeSubjectValue(value: string) {
  return encodeURIComponent(value)
}

function subjectPart(key: string, value: string | undefined) {
  if (!value) return undefined
  return `${key}:${encodeSubjectValue(value)}`
}

function buildMetasearchPriceAccuracySubject(params: SubjectParams) {
  return [
    subjectPart('offerId', params.offerId),
    subjectPart('rateType', params.rateType),
    subjectPart('supplier', params.supplier),
    subjectPart('pageType', params.pageType),
    subjectPart('metasearchRoomRateId', params.metasearchRoomRateId),
    subjectPart('partnerRoomRateId', params.partnerRoomRateId),
  ].filter((part): part is string => !!part).join('|')
}

export function getMetasearchPriceAccuracyEvent(
  eventData: MetasearchPriceAccuracyEventData,
) {
  return {
    subject: eventData.subject,
    action: eventData.price.toString(),
    category: METASEARCH_PRICE_ACCURACY_CATEGORY,
    type: 'operational' as const,
  }
}

export function getBedbankMetasearchPriceAccuracyEventData({
  offerId,
  pageType,
  metasearchRoomRateId,
  rate,
  price: trackedPrice,
  discountAmount,
}: BedbankEventDataParams): MetasearchPriceAccuracyEventData | undefined {
  if (!rate) return undefined

  const price = trackedPrice ?? getBedbankRatePublicTotal(rate)

  return {
    price: getDiscountedPrice(price, discountAmount),
    subject: buildMetasearchPriceAccuracySubject({
      offerId,
      rateType: 'bedbank',
      supplier: rate.supplierCode,
      pageType,
      metasearchRoomRateId,
      partnerRoomRateId: rate.id,
    }),
  }
}

function getPropertyRateSupplier(rate: App.EnrichedPropertyRate) {
  switch (rate.service.type) {
    case 'lpc':
      return 'lpc'
    case 'bedbank':
      return rate.service.supplierCode
  }
}

export function getPropertyMetasearchPriceAccuracyEventData({
  offerId,
  pageType,
  metasearchRoomRateId,
  rate,
  discountAmount,
}: PropertyEventDataParams): MetasearchPriceAccuracyEventData | undefined {
  if (!rate) return undefined

  const priceBeforeDiscount = rate.totals.displayPrice

  return {
    price: getDiscountedPrice(priceBeforeDiscount, discountAmount),
    subject: buildMetasearchPriceAccuracySubject({
      offerId,
      rateType: rate.service.type,
      supplier: getPropertyRateSupplier(rate),
      pageType,
      metasearchRoomRateId,
      partnerRoomRateId: rate.service.rateId,
    }),
  }
}
